import PropTypes from "prop-types"
import React, { useCallback, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import {
  EditJobTemplateLineItemAction,
  RemoveJobTemplateLineItemAction,
} from "src/actions/job_template_line_items"
import { buildReactivateProductAction } from "src/actions/products"
import { useMarina } from "src/contexts/MarinaContext"
import JobFormFields from "src/main/Service/Manage/WorkOrders/JobFormFields"
import AddLineItemButtons from "src/main/Service/Manage/shared/AddLineItemButtons"
import InactiveProductsNotice from "src/main/Service/Manage/shared/InactiveProductsNotice"
import JobLineItemForm from "src/main/Service/Manage/shared/JobLineItemForm"
import { buildJobLineItemColumns } from "src/main/Service/Manage/shared/jobLineItemColumns"
import { useReactivateLineItemProduct } from "src/main/Service/Manage/shared/use_reactivate_line_item_product"
import { v4 as uuidv4 } from "uuid"

import Button from "src/components/Button"
import DataTableV2 from "src/components/DataTableV2"
import ActionConfirmModal from "src/components/DataTableV2/ActionConfirmModal"
import Tooltip from "src/components/Tooltip"

import { archiveJobTemplate } from "src/api/Service"
import {
  createJobTemplate,
  getJobTemplate,
  updateJobTemplate,
} from "src/api/Service/JobTemplates"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { computeSubtotal } from "../shared/helpers"
import { CREATED, DELETED, EDITED } from "./amplitude_events"

const isFixedByLength = (product) => product?.pricingStructure === "by_length"

const buildInitialItems = (lineItems = []) =>
  lineItems.map((li) => ({
    id: li.id,
    clientId: uuidv4(),
    name: li.product.name,
    pricePerUnit: li.product.pricePerUnit,
    pricingStructure: li.product.pricingStructure,
    quantity: Number(li.quantity),
    subtotal: li.subtotal,
    product: li.product,
    productId: li.productId,
  }))

// Inner component rendered only once template data is resolved
const JobTemplateFormContent = ({ template, isEdit }) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
  const [lineItems, setLineItems] = useState(() =>
    buildInitialItems(template?.lineItems)
  )
  const [lineItemFormState, setLineItemFormState] = useState({ mode: "closed" })

  const markProductActive = useCallback((productId) => {
    setLineItems((items) =>
      items.map((item) =>
        item.productId === productId
          ? { ...item, product: { ...item.product, active: true } }
          : item
      )
    )
  }, [])

  const { reactivate, reactivatingProductId } = useReactivateLineItemProduct({
    onReactivated: markProductActive,
  })

  const activeLineItems = lineItems.filter((li) => !li._destroy)

  const inactiveCount = useMemo(
    () =>
      activeLineItems.filter((item) => item.product?.active === false).length,
    [activeLineItems]
  )
  const hasInactiveItems = inactiveCount > 0

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: template?.name ?? "",
      description: template?.description ?? "",
    },
  })

  const lineItemCount = activeLineItems.length

  const laborHours = useMemo(
    () =>
      activeLineItems
        .filter((li) => li.product?.productType === "labor")
        .reduce((sum, li) => sum + (Number(li.quantity) || 0), 0),
    [activeLineItems]
  )

  const lineItemsSubtotal = useMemo(() => {
    return activeLineItems.reduce(
      (accum, item) => accum + (item.subtotal ?? 0),
      0
    )
  }, [activeLineItems])

  const hasByLengthItem = activeLineItems.some(
    (li) => li.product?.pricingStructure === "by_length"
  )

  const backUrl = `/manage/${marinaSlug}/service/job_templates`

  const { mutate: archive, isLoading: isArchiving } = useMutation(
    () => archiveJobTemplate(template.id),
    {
      onSuccess: (data) => {
        setIsArchiveModalOpen(false)
        queryClient.invalidateQueries(["jobTemplates", marinaSlug])
        showToast(data.message, { type: "success" })
        tracker.trackEvent(DELETED, {})
        window.location.href = backUrl
      },
      onError: (error) => {
        showToast(error.message || "Failed to archive template.", {
          type: "error",
        })
      },
    }
  )

  const { mutate: save, isLoading } = useMutation(
    (templateData) =>
      isEdit
        ? updateJobTemplate(marinaSlug, template.id, templateData)
        : createJobTemplate(marinaSlug, templateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["jobTemplates", marinaSlug])
        if (isEdit) {
          tracker.trackEvent(EDITED, { line_items_count_after: lineItemCount })
        } else {
          tracker.trackEvent(CREATED, {
            line_items_count: lineItemCount,
            source: "management_page",
          })
        }
        showToast(
          isEdit
            ? "Template saved successfully."
            : "Template created successfully.",
          { type: "success" }
        )
        window.location.href = backUrl
      },
      onError: (error) => {
        showToast(error.message || "Failed to save template.", {
          type: "error",
        })
      },
    }
  )

  const onSubmit = (formData) => {
    save({
      name: formData.name,
      description: formData.description,
      lineItems: lineItems.map(({ id, productId, quantity, _destroy }) => ({
        id,
        productId,
        quantity,
        _destroy: !!_destroy,
      })),
    })
  }

  const handleLineItemRemoval = useCallback((row) => {
    setLineItems((items) =>
      items.map((item) =>
        item.clientId === row.id ? { ...item, _destroy: true } : item
      )
    )
  }, [])

  const isFormOpen = lineItemFormState.mode !== "closed"

  const lineItemTableColumns = useMemo(
    () =>
      buildJobLineItemColumns({
        presentmentCurrency,
        templateVariant: true,
      }),
    [presentmentCurrency]
  )

  const lineItemsTableData = useMemo(
    () =>
      activeLineItems.map((item) => ({
        ...item,
        id: item.clientId,
      })),
    [activeLineItems]
  )

  const lineItemTableActions = useMemo(
    () => [
      EditJobTemplateLineItemAction({
        disabled: () => isFormOpen,
        onClickSingle: (row) =>
          setLineItemFormState({ mode: "edit", clientId: row.id }),
      }),
      buildReactivateProductAction({
        canReactivate: !!template?.canReactivateProducts,
        onReactivate: reactivate,
        reactivatingProductId,
        disabled: () => isFormOpen,
      }),
      RemoveJobTemplateLineItemAction({
        disabled: () => isFormOpen,
        onClickSingle: (row) => handleLineItemRemoval(row),
      }),
    ],
    [
      isFormOpen,
      handleLineItemRemoval,
      template,
      reactivate,
      reactivatingProductId,
    ]
  )

  const editingItem =
    lineItemFormState.mode === "edit"
      ? lineItems.find((item) => item.clientId === lineItemFormState.clientId)
      : null

  const initialLineItemValues = editingItem
    ? {
        pricePerUnit: editingItem.product.pricePerUnit,
        product: editingItem.product,
        productId: editingItem.product.id,
        quantity: editingItem.quantity,
      }
    : undefined

  const lineItemFormType = (() => {
    if (lineItemFormState.mode === "add") {
      return lineItemFormState.itemType
    }
    return editingItem?.product?.productType === "labor"
      ? "service_labor"
      : "service_parts"
  })()

  const handleLineItemSubmit = useCallback(
    ({ productId, quantity, product }) => {
      const subtotal = isFixedByLength(product)
        ? null
        : computeSubtotal({
            pricePerUnit: product.pricePerUnit,
            quantity,
            pricePrecision: product.pricePrecision,
          })

      if (lineItemFormState.mode === "edit") {
        setLineItems((items) =>
          items.map((item) =>
            item.clientId === lineItemFormState.clientId
              ? {
                  ...item,
                  productId,
                  quantity,
                  product,
                  subtotal,
                }
              : item
          )
        )
      } else {
        setLineItems((items) => [
          ...items,
          {
            clientId: uuidv4(),
            productId,
            pricePerUnit: product.pricePerUnit,
            pricingStructure: product.pricingStructure,
            quantity,
            product,
            subtotal,
          },
        ])
      }

      setLineItemFormState({ mode: "closed" })
    },
    [lineItemFormState]
  )

  const handleFormCancel = useCallback(
    () => setLineItemFormState({ mode: "closed" }),
    []
  )

  const updatedDate = template?.updatedAt
    ? new Date(template.updatedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null

  const renderBackToTemplatesLink = () => (
    <a
      href={backUrl}
      className="mb-3 flex w-fit items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
    >
      <i className="fa-kit fa-arrow-left text-xs" />
      Back to Templates
    </a>
  )
  return (
    <div className="px-6 pb-12">
      {renderBackToTemplatesLink()}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? template?.name ?? "Edit Template" : "New Job Template"}
          </h1>
          {isEdit && updatedDate && (
            <p className="mt-1 text-sm text-gray-500">Updated {updatedDate}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {isEdit && (
            <Button
              variant="secondary"
              type="button"
              onClick={() => setIsArchiveModalOpen(true)}
              isLoading={isArchiving}
              disabled={isLoading || isArchiving}
            >
              Archive
            </Button>
          )}
          <Button
            variant="secondary"
            type="button"
            onClick={() => (window.location.href = backUrl)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit(onSubmit)}
            isLoading={isLoading}
            disabled={isLoading || isFormOpen}
          >
            {isEdit ? "Save Changes" : "Create Template"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 mt-0 text-base font-semibold text-gray-900">
              Details
            </h2>
            <div className="flex flex-col gap-4">
              <JobFormFields control={control} errors={errors} />
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 mt-0 text-base font-semibold text-gray-900">
              {activeLineItems.length > 0
                ? `Line Items (${activeLineItems.length})`
                : "Line Items"}
            </h2>

            {hasInactiveItems && (
              <InactiveProductsNotice
                count={inactiveCount}
                contextLabel="template"
                resolutionTail="from this template."
                canReactivate={!!template?.canReactivateProducts}
                marinaSlug={marinaSlug}
                className="mb-4"
              />
            )}

            {activeLineItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <i className="fa-kit fa-layers text-3xl text-gray-300" />
                <p className="mt-3 text-sm font-semibold text-gray-700">
                  No line items
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Add labor or parts that make up this job.
                </p>
              </div>
            ) : (
              <DataTableV2
                name="job-template-form-line-items"
                columns={lineItemTableColumns}
                data={lineItemsTableData}
                actions={lineItemTableActions}
                hideSettings
                footerRow={{
                  productName: "Subtotal",
                  subtotal: hasByLengthItem ? (
                    <span className="flex items-center gap-1 font-semibold">
                      {formatCurrencyFromMinorUnits({
                        amount: lineItemsSubtotal,
                        currency: presentmentCurrency,
                        precision: 2,
                      })}
                      <span className="ml-2 font-normal">
                        <Tooltip
                          placement="bottom"
                          maxWidth="350px"
                          variant="dark"
                          text="Estimate excludes per ft or per sqft items — these are automatically calculated when this template is applied to a specific boat on a work order."
                        >
                          <i className="fa-kit fa-warning-outline cursor-pointer text-gray-600" />
                        </Tooltip>
                      </span>
                    </span>
                  ) : (
                    formatCurrencyFromMinorUnits({
                      amount: lineItemsSubtotal,
                      currency: presentmentCurrency,
                      precision: 2,
                    })
                  ),
                }}
              />
            )}
            {isFormOpen && (
              <div className="mt-4 rounded-lg border border-blue-200 p-4">
                <JobLineItemForm
                  availability={lineItemFormType}
                  initialValues={initialLineItemValues}
                  onSubmit={handleLineItemSubmit}
                  onCancel={handleFormCancel}
                  submitLabel={
                    lineItemFormState.mode === "edit" ? "Update" : "Add"
                  }
                  templateRendering
                />
              </div>
            )}

            <div className="mt-4">
              <AddLineItemButtons
                disabled={isFormOpen}
                onAddLabor={() =>
                  setLineItemFormState({
                    mode: "add",
                    itemType: "service_labor",
                  })
                }
                onAddParts={() =>
                  setLineItemFormState({
                    mode: "add",
                    itemType: "service_parts",
                  })
                }
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 mt-0 text-base font-semibold text-gray-900">
              Summary
            </h2>
            <dl className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <div className="text-gray-600">Line items</div>
                <div className="text-gray-900">{lineItemCount}</div>
              </div>
              <div className="flex justify-between">
                <div className="text-gray-600">Labor hours</div>
                <div className="text-gray-900">
                  {laborHours > 0 ? `${laborHours} hr` : "0 hr"}
                </div>
              </div>
              <div className="flex justify-between">
                <div className="text-gray-600">Default subtotal</div>
                <div className="text-gray-900">
                  {formatCurrencyFromMinorUnits({
                    amount: lineItemsSubtotal,
                    currency: presentmentCurrency,
                    precision: 2,
                  })}
                </div>
              </div>
            </dl>
            <p className="mt-4 text-sm text-gray-600">
              Quantities marked per ft or per sqft auto-scale to the boat when
              applied to a work order.
            </p>
          </div>
        </div>
      </div>
      <ActionConfirmModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={() => archive()}
        title="Archive Job Template"
        message={`Are you sure you want to archive the ${template?.name} template?`}
        confirmLabel="Archive Job Template"
        danger
        isLoading={isArchiving}
      />
    </div>
  )
}

JobTemplateFormContent.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    lineItems: PropTypes.array,
    canReactivateProducts: PropTypes.bool,
  }),
  isEdit: PropTypes.bool.isRequired,
}

const JobTemplate = ({ jobTemplateId }) => {
  const { marinaSlug } = useMarina()
  const isEdit = !!jobTemplateId

  const { data: response, isLoading } = useQuery(
    ["jobTemplate", jobTemplateId],
    () => getJobTemplate(marinaSlug, jobTemplateId),
    { enabled: isEdit }
  )

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <i className="fa-kit fa-loading animate-spin text-2xl" />
      </div>
    )
  }

  const template = isEdit ? response?.data : null

  return <JobTemplateFormContent template={template} isEdit={isEdit} />
}

JobTemplate.propTypes = {
  jobTemplateId: PropTypes.number,
}

export default JobTemplate
